import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import Modal from 'Components/UI/Modal';
import Button from 'Components/UI/Button';
import Tabs from 'Components/UI/Tabs/Tabs';
import UsuariosConvidadosTab from '../../components/Tabs/UsuariosConvidadosTab';
import PermissoesUsuariosTab from '../../components/Tabs/PermissoesUsuariosTab';
import { IInvitedUser } from 'Data/interfaces/User/IInvitedUser';
import { ISharedUser } from 'Data/interfaces/User/ISharedUser';
import { ObraActions } from 'Store/Obra/Obra.actions';
import IBindUsersConstructionSiteRequest from 'Data/interfaces/User/IBindUsersConstructionSiteRequest';
import { Skeleton } from 'antd';
import { IUserData } from 'Data/interfaces/User/IUserData';
import { Mixpanel } from 'Utils/MixPanel';
import { getCurrentTenant, getTenants } from 'Store/Tenant/Tenant.selector';

interface IUsuariosEPermissoes {
  isVisible?: boolean;
  onCancel?: () => void;
  maxUsers?: number;
  firstInvitedUsers?: IInvitedUser[];
}

const UsuariosEPermissoes: FC<Props> = (props) => {
  const { t } = useTranslation('obras');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tenants = useSelector(getTenants);
  const currentTenant = useSelector(getCurrentTenant);

  const [invitedUsers, setInvitedUsers] = useState<IInvitedUser[]>([]);
  const [sharedUsers, setSharedUsers] = useState<ISharedUser[]>([]);
  const [isSendingData, setSendingData] = useState<boolean>(false);
  const [newInvitedsUserandPerm, setNewInvitedsUserandPerm] = useState(0);

  if (isSendingData && !props.isLoading) {
    setSendingData(false);
    if (props.onCancel) {
      props.onCancel();
    }
  }

  const tenantUsers = () => {
    return [
      ...props.SharedUsers?.map((sharedUser: any) => sharedUser.User || {} as IUserData) || [],
      ...props.UnsharedUsers || [],
    ];
  };

  const updateUsers = (data: any) => {
    setInvitedUsers(data.InvitedUsers);
    setSharedUsers(data.SharedUsers);
  };

  const openUpgradePremium = () => {
    Mixpanel.track({
      name: 'CALL_T0_ACTION',
      props: {
        origin: 'convidar-usuarios-modal',
        originPath: window.location.pathname,
      },
      userInfo: props.userInfo,
      currentListTenant: currentTenant,
      tenantIds: tenants && tenants.length > 0
        ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
        : undefined,
    });

    if (props.currentRole === 1) {
      if (props.onCancel) {
        props.onCancel();
      }
      navigate('/faturamento/planos');
    } else {
      if (props.onCancel) {
        props.onCancel();
      }
      window.open('https://bim.maletadoengenheiro.com.br/seja-premium');
    }
  };

  const getTabs = () => {
    const tabs = [
      {
        title: t('usuariosEPermissoes.tabUsers'), key: 'tabUsuario',
        children: (
          <UsuariosConvidadosTab
            tenantRoles={props.tenantRoles}
            onConfirm={openUpgradePremium}
            TenantSharedUsers={tenantUsers()}
            sharedUsers={sharedUsers}
            invitedUsers={invitedUsers}
            changeUsers={updateUsers}
            currentRole={props.currentRole}
            numberInvitedAllUsers={props.QtyActiveUsers}
            maxUsers={props.maxUsers}
            csId={props.ConstructionSiteId}
            firstInvitedUsers={props.firstInvitedUsers}
            isLoading={props.isLoading}
          />
        ),
      },
    ];
    if (invitedUsers.length || sharedUsers.length) {
      tabs.push({
        title: t('usuariosEPermissoes.tabPermissions'),
        key: 'tabPermissoes',
        children: props.isLoading ? (
          <div style={{ padding: 20 }}>
            <Skeleton loading={true} paragraph={{ rows: 4 }} />
          </div>
        ) : (
          <PermissoesUsuariosTab
            invitedUsers={invitedUsers}
            sharedUsers={sharedUsers}
            changeUsers={updateUsers}
          />
        ),
      });
    }

    return tabs;
  };

  const bindUsers = () => {
    const filterInvitedUsers = (invitedUsers.filter((user) => props.allUsers?.tenantInvitedUsers
      .some((u) => u.InvitedUserEmail === user.Email)));
    const filterSharedUsers = props.allUsers?.tenantUsers?.filter((user) => user.User.Id !== props.userInfo?.Id);
    const isFirstUser = ((filterInvitedUsers?.length || 0) <= 0) &&
      ((filterSharedUsers?.length || 0) <= 0) &&
      invitedUsers.length > 0;

    sharedUsers.forEach((sharedUser) => sharedUser.UserId = sharedUser.UserFk);
    const request: IBindUsersConstructionSiteRequest = {
      ConstructionSiteId: props.ConstructionSiteId || 0,
      InvitedUsers: invitedUsers,
      SharedUsers: sharedUsers,
      newInvitedUsers: newInvitedsUserandPerm,
      firstUserTracking: isFirstUser,
      userInfoTracking: props.userInfo,
      currentTenantTracking: currentTenant,
      tenantIds: tenants && tenants.length > 0
        ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
        : undefined,
    };

    dispatch(ObraActions.bindUsersConstructionSite(request));
    setSendingData(true);
  };

  useEffect(() => {
    setInvitedUsers([...props.InvitedUsers || []]);
    setSharedUsers([...props.SharedUsers || []]);
  }, [props]);

  useEffect(() => {
    if (invitedUsers && props.firstInvitedUsers) {
      setNewInvitedsUserandPerm(invitedUsers.filter((user) => {
        return !props.firstInvitedUsers?.map((userAll) => userAll.Email).includes(user.Email);
      })?.length);
    }
    if (invitedUsers && !props.firstInvitedUsers) {
      setNewInvitedsUserandPerm(invitedUsers.length);
    }
  }, [invitedUsers, props.firstInvitedUsers]);

  if (props.isVisible) {
    return (
      <Modal
        width={860}
        title={t('usuariosEPermissoes.modalTitle')}
        visible={props.isVisible}
        onCancel={props.onCancel}
        footerButtons={(
          <>
            <Button onClick={props.onCancel}>
              <span style={{ padding: '0 35px' }}>{t('usuariosEPermissoes.cancel')}</span>
            </Button>
            <Button loading={props.isLoading} type='primary' onClick={bindUsers}>
              <span style={{ padding: '0 35px' }}>{t('usuariosEPermissoes.save')}</span>
            </Button>
          </>
        )}
      >
        <Tabs tabs={getTabs()} />
      </Modal>
    );
  }

  return <></>;
};

const mapState = (state: IGlobalReducerState) => ({
  ...state.obra.Users,
  currentRole: state.auth.userInfo?.CurrentRoleFk,
  QtyActiveUsers: state.auth.userLimitsActive?.QtyActiveUsers,
  isLoading: state.obra.isLoadingGetUsersConstruction,
  tenantRoles: state.tenant.TenantRoles || [],
  allUsers: state.tenant.UsersWithConstruction,
  userInfo: state.auth.userInfo,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & IUsuariosEPermissoes;

export default connector(UsuariosEPermissoes);
